<template>
  <AppLayout title="Meine Bestellungen">
    <div class="min-h-screen bg-gray-50">
      <!-- Header -->
      <div class="bg-white shadow">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div class="flex justify-between items-center">
            <div>
              <h1 class="text-2xl font-bold text-gray-900">{{ $t('Meine Bestellungen & Vorbestellungen') }}</h1>
              <p class="text-sm text-gray-600 mt-1">{{ $t('Übersicht über alle Ihre Bestellungen und Rechnungen') }}</p>
            </div>
            <div class="flex space-x-3">
              <Link
                :href="route('cart')"
                class="btn btn-sonnenglas"
              >
                {{ $t('Neue Bestellung') }}
                <svg class="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
              </Link>
              <Link 
                :href="route('customerAccount')" 
                class="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                {{ $t('Zurück zum Konto') }}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <!-- Filter and Search -->
        <div class="bg-white shadow rounded-lg mb-6">
          <div class="p-6">
            <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
              <div class="flex space-x-4">
                <select 
                  v-model="filterType" 
                  class="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500"
                >
                  <option value="all">{{ $t('Alle Bestellungen') }}</option>
                  <option value="orders">{{ $t('Nur Bestellungen') }}</option>
                  <option value="preorders">{{ $t('Nur Vorbestellungen') }}</option>
                </select>
                <select 
                  v-model="sortBy" 
                  class="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500"
                >
                  <option value="date_desc">{{ $t('Neueste zuerst') }}</option>
                  <option value="date_asc">{{ $t('Älteste zuerst') }}</option>
                  <option value="amount_desc">{{ $t('Höchster Betrag') }}</option>
                  <option value="amount_asc">{{ $t('Niedrigster Betrag') }}</option>
                </select>
              </div>
              <div class="relative">
                <input 
                  v-model="searchQuery"
                  type="text" 
                  :placeholder="$t('Bestellnummer suchen...')"
                  class="border border-gray-300 rounded-lg pl-10 pr-4 py-2 text-sm focus:ring-sonnenglas-500 focus:border-sonnenglas-500 w-64"
                >
                <svg class="absolute left-3 top-2.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
              </div>
            </div>
          </div>
        </div>

        <!-- Pre-Orders Section -->
        <div v-if="filteredPreOrders.length > 0" class="mb-8">
          <h2 class="text-lg font-semibold text-gray-900 mb-4">{{ $t('Aktuelle Vorbestellungen') }}</h2>
          <div class="space-y-4">
            <div 
              v-for="order in filteredPreOrders" 
              :key="order.id"
              class="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-lg shadow-sm"
            >
              <OrderCard :order="order" :is-preorder="true" />
            </div>
          </div>
        </div>

        <!-- Orders Section -->
        <div v-if="filteredOrders.length > 0">
          <h2 class="text-lg font-semibold text-gray-900 mb-4">{{ $t('Bestellungen') }}</h2>
          <div class="space-y-4">
            <div 
              v-for="order in filteredOrders" 
              :key="order.id"
              class="bg-white border border-gray-200 rounded-lg shadow-sm"
            >
              <OrderCard :order="order" :is-preorder="false" />
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="orders.links" class="mt-8 flex justify-center">
            <nav class="flex items-center space-x-2">
              <template v-for="link in orders.links" :key="link.label">
                <Link 
                  v-if="link.url"
                  :href="link.url"
                  :class="[
                    'px-3 py-2 text-sm rounded-lg transition-colors',
                    link.active 
                      ? 'bg-sonnenglas-600 text-white' 
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  ]"
                  v-html="link.label"
                />
                <span 
                  v-else
                  :class="[
                    'px-3 py-2 text-sm rounded-lg',
                    'bg-gray-100 text-gray-400 cursor-not-allowed'
                  ]"
                  v-html="link.label"
                />
              </template>
            </nav>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="filteredOrders.length === 0 && filteredPreOrders.length === 0" class="text-center py-12">
          <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
          </svg>
          <h3 class="mt-2 text-sm font-medium text-gray-900">{{ $t('Keine Bestellungen gefunden') }}</h3>
          <p class="mt-1 text-sm text-gray-500">{{ $t('Sie haben noch keine Bestellungen aufgegeben.') }}</p>
          <div class="mt-6">
            <Link
              :href="route('products')"
              class="btn btn-sonnenglas"
            >
              {{ $t('Jetzt einkaufen') }}
            </Link>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { Link } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import OrderCard from '@/components/OrderCard.vue'

interface OrderItem {
  quantity: number
  name: string
}

interface Order {
  id: number
  order_number: string
  created_at: string
  data: {
    items: OrderItem[]
    total: number
    currency: string
    shipped?: boolean
    shipped_at?: string
    ordered_at?: string
    payment_method?: string
    tracking_codes?: string[]
    proforma?: boolean
    invoice_url?: string
    pallet_shipping?: boolean
    due_in?: number
  }
  children?: Order[]
  is_paid?: boolean
}

interface PaginatedOrders {
  data: Order[]
  links?: Array<{
    url?: string
    label: string
    active: boolean
  }>
}

interface Customer {
  id: number
  name: string
}

interface Props {
  customer: Customer
  orders: PaginatedOrders
  newPreOrders: PaginatedOrders
}

const props = defineProps<Props>()

// Reactive filter state
const filterType = ref<'all' | 'orders' | 'preorders'>('all')
const sortBy = ref<'date_desc' | 'date_asc' | 'amount_desc' | 'amount_asc'>('date_desc')
const searchQuery = ref('')

// Computed filtered and sorted orders
const filteredOrders = computed(() => {
  if (filterType.value === 'preorders') return []
  
  let filtered = props.orders.data.filter(order => {
    if (searchQuery.value) {
      return order.order_number.toLowerCase().includes(searchQuery.value.toLowerCase())
    }
    return true
  })
  
  return sortOrders(filtered)
})

const filteredPreOrders = computed(() => {
  if (filterType.value === 'orders') return []
  
  let filtered = props.newPreOrders.data.filter(order => {
    if (searchQuery.value) {
      return order.order_number.toLowerCase().includes(searchQuery.value.toLowerCase())
    }
    return true
  })
  
  return sortOrders(filtered)
})

const sortOrders = (orders: Order[]) => {
  return [...orders].sort((a, b) => {
    switch (sortBy.value) {
      case 'date_asc':
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      case 'date_desc':
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      case 'amount_asc':
        return (a.data.total || 0) - (b.data.total || 0)
      case 'amount_desc':
        return (b.data.total || 0) - (a.data.total || 0)
      default:
        return 0
    }
  })
}
</script>